import { BadgeDollarSign, Save } from "lucide-react";
import { PageHeader } from "@tribunalea/ui";
import { useEffect, useState } from "react";

function editableCatalog(catalog) {
  return {
    ...catalog,
    items: catalog.items.map((item) => ({
      ...item,
      priceArs: item.priceMinorUnits === null
        ? ""
        : String(item.priceMinorUnits / 100),
    })),
    customPurchase: {
      ...catalog.customPurchase,
      pricePerStepArs: String(
        catalog.customPurchase.pricePerStepMinorUnits / 100,
      ),
    },
  };
}

export function BillingPricingAdminPage({ api }) {
  const [catalog, setCatalog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;
    api.billingCatalog()
      .then((result) => active && setCatalog(editableCatalog(result)))
      .catch((requestError) => active && setError(requestError.message));
    return () => { active = false; };
  }, [api]);

  function updateItem(key, changes) {
    setCatalog((current) => ({
      ...current,
      items: current.items.map((item) => (
        item.key === key ? { ...item, ...changes } : item
      )),
    }));
  }

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await api.updateBillingCatalog({
        items: catalog.items
          .filter(({ kind }) => kind !== "contact")
          .map(({ key, priceArs, active }) => ({
            key,
            active,
            priceMinorUnits: Math.round(Number(priceArs) * 100),
          })),
        customPurchase: {
          enabled: catalog.customPurchase.enabled,
          minimumCredits: Number(catalog.customPurchase.minimumCredits),
          maximumCredits: Number(catalog.customPurchase.maximumCredits),
          stepCredits: Number(catalog.customPurchase.stepCredits),
          pricePerStepMinorUnits: Math.round(
            Number(catalog.customPurchase.pricePerStepArs) * 100,
          ),
        },
      });
      setCatalog(editableCatalog(result.catalog));
      const failed = result.subscriptionPriceUpdates.filter(
        ({ status }) => status === "failed",
      ).length;
      setNotice(failed
        ? `Precios guardados; ${failed} suscripción(es) requieren reintento de sincronización.`
        : "Precios guardados y suscripciones activas actualizadas.");
    } catch (requestError) {
      setError(requestError.message || "No pudimos actualizar los precios.");
    } finally {
      setBusy(false);
    }
  }

  if (!catalog) {
    return <div className="support-access-state">{error || "Cargando precios…"}</div>;
  }

  const sections = [
    ["subscription", "Suscripciones mensuales"],
    ["one_time", "Compras únicas"],
  ];

  return (
    <div className="billing-pricing-admin-page">
      <PageHeader
        hero
        eyebrow="Administración · Facturación"
        title="Precios de créditos"
        description="Los cambios de suscripción se aplican a clientes existentes en su próxima renovación."
      />
      {error ? <div className="support-access-error" role="alert">{error}</div> : null}
      {notice ? <div className="invitation-success" role="status">{notice}</div> : null}
      <form onSubmit={save} className="billing-pricing-form">
        {sections.map(([kind, label]) => (
          <section key={kind}>
            <header><BadgeDollarSign size={19} /><div><h2>{label}</h2><p>Importes finales expresados en pesos argentinos.</p></div></header>
            <div className="billing-pricing-grid">
              {catalog.items.filter((item) => item.kind === kind).map((item) => (
                <label key={item.key}>
                  <span>{item.credits.toLocaleString("es-AR")} créditos</span>
                  <span className="billing-price-input"><em>$</em><input
                    required
                    type="number"
                    min="1"
                    step="1"
                    value={item.priceArs}
                    onChange={(event) => updateItem(item.key, {
                      priceArs: event.target.value,
                    })}
                  /></span>
                  <span className="billing-active-toggle"><input
                    type="checkbox"
                    checked={item.active}
                    onChange={(event) => updateItem(item.key, {
                      active: event.target.checked,
                    })}
                  /> Disponible</span>
                </label>
              ))}
            </div>
          </section>
        ))}
        <section>
          <header><BadgeDollarSign size={19} /><div><h2>Compra personalizada</h2><p>Cantidad permitida y precio por cada bloque.</p></div></header>
          <div className="billing-custom-grid">
            <label>Mínimo<input type="number" min="1" value={catalog.customPurchase.minimumCredits} onChange={(event) => setCatalog((current) => ({ ...current, customPurchase: { ...current.customPurchase, minimumCredits: event.target.value } }))} /></label>
            <label>Máximo<input type="number" min="1" value={catalog.customPurchase.maximumCredits} onChange={(event) => setCatalog((current) => ({ ...current, customPurchase: { ...current.customPurchase, maximumCredits: event.target.value } }))} /></label>
            <label>Bloque<input type="number" min="1" value={catalog.customPurchase.stepCredits} onChange={(event) => setCatalog((current) => ({ ...current, customPurchase: { ...current.customPurchase, stepCredits: event.target.value } }))} /></label>
            <label>Precio por bloque<input type="number" min="1" value={catalog.customPurchase.pricePerStepArs} onChange={(event) => setCatalog((current) => ({ ...current, customPurchase: { ...current.customPurchase, pricePerStepArs: event.target.value } }))} /></label>
          </div>
        </section>
        <button type="submit" className="primary-button" disabled={busy}>
          <Save size={17} /> {busy ? "Guardando…" : "Guardar precios"}
        </button>
      </form>
    </div>
  );
}
